// src/admin/components/tenants/TenantDetailView.jsx - Showroom Client & Contract Management
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  RiArrowLeftLine,
  RiPencilLine,
  RiDeleteBin6Line,
  RiAddLine,
  RiBuilding3Line,
  RiMoneyDollarCircleLine,
  RiWallet3Line,
  RiHistoryLine,
  RiMapPinLine,
  RiPhoneLine,
  RiMailLine,
  RiContactsLine,
  RiFileTextLine,
  RiAlertLine,
  RiRefreshLine,
  RiCloseCircleLine,
  RiCalendarLine,
  RiCalendar2Line,
  RiBox3Line,
  RiBriefcaseLine,
  RiCalendarCheckLine,
  RiFileListLine,
  RiShieldCheckLine
} from 'react-icons/ri';
import {
  getTenant,
  getRentPayments,
  recordRentPayment,
  formatTenantDisplayName,
  formatCurrency,
  formatDate,
  getRentStatusColor,
  getLeaseStatusColor,
  calculateLeaseSummary,
  isLeaseExpired,
  extendLease,
  renewLease,
  transferApartment
} from '../../../models/tenantModel';
import { getAllApartments } from '../../../models/apartmentModel';
import { tenantsAPI } from '../../../services/api';
import { useTheme } from '../../../contexts/ThemeContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const TenantDetailView = () => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [leases, setLeases] = useState([]);
  const [activeLeases, setActiveLeases] = useState([]);
  const [payments, setPayments] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  const formatDateForBackend = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const calculateEndOfMonth = (startDate) => {
    const start = new Date(startDate);
    const end = new Date(start);
    end.setMonth(end.getMonth() + 1);
    end.setDate(0); 
    return formatDateForBackend(end);
  };

  // Forms & Modals State
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    leaseId: '',
    amount: '',
    paymentDate: '',
    paymentPeriodStart: '',
    paymentPeriodEnd: '',
    paymentMethod: 'rent',
    notes: ''
  });

  const [endLeaseModal, setEndLeaseModal] = useState(false);
  const [endingLease, setEndingLease] = useState(false);

  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extendingLease, setExtendingLease] = useState(false);
  const [extensionForm, setExtensionForm] = useState({ newEndDate: '' });

  const [showRenewModal, setShowRenewModal] = useState(false);
  const [renewingLease, setRenewingLease] = useState(false);
  const [apartments, setApartments] = useState([]);
  const [renewForm, setRenewForm] = useState({
    apartmentId: '',
    rent: '',
    discount: '0',
    contractStartDate: '',
    contractEndDate: '',
    paymentDate: '',
    serviceFee: '0',
    officeCommission: '0',
    insurance: '0'
  });

  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferringApartment, setTransferringApartment] = useState(false);
  const [transferForm, setTransferForm] = useState({
    apartmentId: '',
    rent: '',
    deposit: '',
    transferDate: formatDateForBackend(new Date())
  });

  useEffect(() => {
    const fetchTenantData = async () => {
      try {
        setLoading(true);
        setError(null);

        const tenantResponse = await getTenant(id);
        const currentTenant = tenantResponse.tenant;
        setTenant(currentTenant);

        if (currentTenant.apartmentId) {
          const leaseData = {
            id: currentTenant.id,
            status: currentTenant.paymentEndDate ? 'ended' : 'active',
            start_date: currentTenant.contractStartDate,
            end_date: currentTenant.contractEndDate,
            actual_end_date: currentTenant.nextBillingDate,
            monthly_rent: currentTenant.rent,
            rent_after_discount: currentTenant.rentAfterDiscount,
            security_deposit: currentTenant.insurance,
            property_number: currentTenant.apartmentNumber,
            building_name: currentTenant.buildingName,
            property_id: currentTenant.apartmentId
          };
          setLeases([leaseData]);
          setActiveLeases(isLeaseExpired(currentTenant) ? [] : [leaseData]);
        } else {
          setLeases([]);
          setActiveLeases([]);
        }

        try {
          const paymentsResponse = await getRentPayments(id);
          const processedPayments = (paymentsResponse.payments || []).map(payment => {
            try {
              let parsedMonths = payment.months;
              if (typeof payment.months === 'string') parsedMonths = JSON.parse(payment.months);
              return { ...payment, months: parsedMonths };
            } catch (err) {
              return { ...payment, months: [] };
            }
          });
          setPayments(processedPayments);
        } catch (err) {
          setPayments([]);
        }

      } catch (err) {
        console.error('Error fetching client data:', err);
        setError(err.message || t('failed_to_load_client_data'));
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchTenantData();
  }, [id, t]);

  const formatPaymentPeriod = (payment) => {
    if (!payment.months) return t('unknown_period');
    try {
      let months = payment.months;
      if (typeof months === 'string') months = JSON.parse(months);
      if (!Array.isArray(months)) return t('invalid_format');
      if (months.length >= 2) return `${formatDate(months[0])} - ${formatDate(months[1])}`;
      return months[0] ? formatDate(months[0]) : t('unknown_period');
    } catch (err) {
      return t('invalid_data');
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
        setError(t('please_enter_valid_payment_amount'));
        setSaving(false); return;
      }
      if (new Date(paymentForm.paymentPeriodEnd) <= new Date(paymentForm.paymentPeriodStart)) {
        setError(t('end_date_must_be_after_start_date'));
        setSaving(false); return;
      }

      const paymentData = {
        amount: parseFloat(paymentForm.amount),
        paymentDate: formatDateForBackend(paymentForm.paymentDate),
        months: [formatDateForBackend(paymentForm.paymentPeriodStart), formatDateForBackend(paymentForm.paymentPeriodEnd)],
        paymentType: paymentForm.paymentMethod,
        notes: paymentForm.notes || null
      };

      await recordRentPayment(tenant.id, paymentData);

      const paymentsResponse = await getRentPayments(tenant.id);
      const processedPayments = (paymentsResponse.payments || []).map(payment => {
        try {
          let parsedMonths = payment.months;
          if (typeof payment.months === 'string') parsedMonths = JSON.parse(payment.months);
          return { ...payment, months: parsedMonths };
        } catch (err) {
          return { ...payment, months: [] };
        }
      });
      setPayments(processedPayments);

      const tenantResponse = await getTenant(tenant.id);
      setTenant(tenantResponse.tenant);

      setPaymentForm({
        leaseId: '', amount: '', paymentDate: formatDateForBackend(new Date()),
        paymentPeriodStart: formatDateForBackend(new Date()),
        paymentPeriodEnd: calculateEndOfMonth(new Date()),
        paymentMethod: 'rent', notes: ''
      });
      setShowPaymentForm(false);
      setError(null);
    } catch (err) {
      setError(err.message || t('failed_to_add_payment'));
    } finally {
      setSaving(false);
    }
  };

  const handleEndLease = async () => {
    try {
      setEndingLease(true);
      await tenantsAPI.endLease(tenant.id);
      const tenantResponse = await getTenant(id);
      const refreshed = tenantResponse.tenant;
      setTenant(refreshed);

      if (refreshed.apartmentId) {
        const leaseData = {
          id: refreshed.id, status: 'ended', start_date: refreshed.contractStartDate,
          end_date: refreshed.contractEndDate, actual_end_date: formatDateForBackend(new Date()),
          monthly_rent: refreshed.rent, rent_after_discount: refreshed.rentAfterDiscount,
          security_deposit: refreshed.insurance, property_number: refreshed.apartmentNumber,
          building_name: refreshed.buildingName, property_id: refreshed.apartmentId
        };
        setLeases([leaseData]);
        setActiveLeases([]);
      } else {
        setLeases([]); setActiveLeases([]);
      }
      setEndLeaseModal(false);
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('failed_to_terminate_contract'));
    } finally {
      setEndingLease(false);
    }
  };

  const handleExtendLease = async (e) => {
    e.preventDefault();
    try {
      setExtendingLease(true);
      await extendLease(tenant.id, extensionForm.newEndDate);
      const response = await getTenant(id);
      setTenant(response.tenant);
      alert(t('contract_extended_success'));
    } catch (err) {
      setError(err.message || t('failed_to_update_tenant_status'));
    } finally {
      setExtendingLease(false);
    }
  };

  const handleRenewLease = async (e) => {
    e.preventDefault();
    try {
      setRenewingLease(true);
      await renewLease(tenant.id, renewForm);
      const response = await getTenant(id);
      setTenant(response.tenant);
      alert(t('contract_renewed_success'));
    } catch (err) {
      setError(err.message || t('failed_to_update_tenant_status'));
    } finally {
      setRenewingLease(false);
    }
  };

  const fetchApartments = async () => {
    try {
      const response = await getAllApartments({ status: 'vacant' });
      setApartments(response.apartments || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleTransferApartment = async (e) => {
    e.preventDefault();
    try {
      setTransferringApartment(true);
      await transferApartment(tenant.id, transferForm);
      const response = await getTenant(id);
      setTenant(response.tenant);
      alert(t('relocated_success'));
    } catch (err) {
      setError(err.message || t('failed_to_relocate_client'));
    } finally {
      setTransferringApartment(false);
    }
  };

  if (loading) {
    return (
      <div className={`rounded-xl border shadow-lg p-16 text-center ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-red-500 border-t-transparent"></div>
          <p className={`text-lg font-medium font-mono ${isDarkMode ? 'text-zinc-300' : 'text-zinc-600'}`}>{t('loading_client_profile')}</p>
        </div>
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className={`rounded-xl border shadow-lg p-12 text-center ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
        <RiAlertLine className={`mx-auto text-6xl mb-4 opacity-80 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
        <h3 className={`text-xl font-bold mb-2 uppercase tracking-wide ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{t('profile_error')}</h3>
        <p className={`mb-6 font-medium ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{error || t('client_not_found')}</p>
        <button onClick={() => navigate('/admin/tenants')} className={`px-6 py-2.5 rounded-lg font-bold uppercase tracking-wider flex items-center justify-center mx-auto ${isDarkMode ? 'bg-zinc-800 text-white hover:bg-zinc-700' : 'bg-zinc-200 text-zinc-800 hover:bg-zinc-300'}`}>
          <RiArrowLeftLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('back_to_roster')}
        </button>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${isRTL ? 'rtl' : 'ltr'}`}>

      {/* Modals */}
      {endLeaseModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className={`rounded-xl border shadow-2xl max-w-md w-full p-6 ${isDarkMode ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-zinc-200'}`}>
            <div className={`flex items-center mb-4 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
              <RiAlertLine size={24} className="me-2" />
              <h3 className="text-lg font-bold uppercase tracking-wider">{t('terminate_contract')}</h3>
            </div>
            <p className={`mb-6 font-medium ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
              {t('terminate_confirmation_desc')}
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setEndLeaseModal(false)} disabled={endingLease} className={`px-5 py-2.5 rounded-lg font-semibold transition-colors ${isDarkMode ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-zinc-200 text-zinc-700 hover:bg-zinc-300'}`}>
                {t('cancel')}
              </button>
              <button onClick={handleEndLease} disabled={endingLease} className={`px-5 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white flex items-center font-semibold shadow-md ${endingLease ? 'opacity-70 cursor-not-allowed' : ''}`}>
                {endingLease ? <><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin me-2" />{t('terminating')}</> : <><RiCloseCircleLine className="me-2" size={18} />{t('terminate')}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Header Card */}
      <div className={`rounded-xl border shadow-lg overflow-hidden ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
        <div className="h-1.5 w-full bg-gradient-to-r from-red-600 via-orange-500 to-red-600" />
        
        <div className={`p-6 border-b ${isDarkMode ? 'bg-zinc-900/50 border-zinc-800' : 'bg-zinc-50 border-zinc-100'}`}>
          <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-6">
            
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin/tenants')} className={`p-2.5 rounded-full shrink-0 transition-colors ${isDarkMode ? 'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700' : 'bg-zinc-200 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-300'}`}>
                <RiArrowLeftLine size={22} className={isRTL ? 'rotate-180' : ''} />
              </button>
              
              <div>
                <div className="flex items-center gap-3">
                  <span className={`inline-flex px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded border ${getRentStatusColor(tenant.rentStatus, isDarkMode)}`}>
                    {tenant.rentStatus ? t(tenant.rentStatus.toLowerCase()) : t('unknown_status')}
                  </span>
                  {tenant.status === 'expired' && (
                    <span className={`inline-flex px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded border ${isDarkMode ? 'bg-red-900/30 text-red-400 border-red-800' : 'bg-red-100 text-red-700 border-red-200'}`}>
                      {t('contract_ended')}
                    </span>
                  )}
                </div>
                
                {tenant.apartmentNumber && (
                  <div className={`flex items-center text-sm font-medium mt-2 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>
                    <RiBox3Line className="me-1.5 text-lg" />
                    <span>
                      {t('unit_number_display', { number: tenant.apartmentNumber })} {tenant.floor ? t('block_number_display', { number: tenant.floor }) : ''} • {tenant.buildingName}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              {tenant.status === 'active' && (
                <button onClick={() => { setExtensionForm({ newEndDate: tenant.contractEndDate }); setShowExtendModal(true); }} className={`px-4 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wider flex items-center transition-all shadow-sm ${isDarkMode ? 'bg-blue-900/30 text-blue-400 border border-blue-900/50 hover:bg-blue-900/50' : 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'}`}>
                  <RiCalendar2Line className="me-2" size={18} /> {t('extend')}
                </button>
              )}

              {tenant.status === 'active' && tenant.apartmentId && (
                <button onClick={() => { fetchApartments(); setTransferForm({ apartmentId: '', rent: tenant.rent || '', deposit: tenant.insurance || '', transferDate: formatDateForBackend(new Date()) }); setShowTransferModal(true); }} className={`px-4 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wider flex items-center transition-all shadow-sm ${isDarkMode ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-900/50 hover:bg-emerald-900/50' : 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'}`}>
                  <RiHistoryLine className="me-2" size={18} /> {t('relocate')}
                </button>
              )}

              {tenant.status === 'expired' && (
                <button onClick={() => { fetchApartments(); setRenewForm({ apartmentId: tenant.apartmentId || '', rent: tenant.rent || '', discount: tenant.discount || '0', contractStartDate: formatDateForBackend(new Date()), contractEndDate: '', paymentDate: formatDateForBackend(new Date()), serviceFee: tenant.serviceFee || '0', officeCommission: tenant.officeCommission || '0', insurance: tenant.insurance || '0' }); setShowRenewModal(true); }} className={`px-4 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wider flex items-center transition-all shadow-sm bg-blue-600 text-white hover:bg-blue-700`}>
                  <RiRefreshLine className="me-2" size={18} /> {t('renew_contract')}
                </button>
              )}

              {tenant.status !== 'expired' && tenant.apartmentId && (
                <button onClick={() => setEndLeaseModal(true)} className={`px-4 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wider flex items-center transition-all shadow-sm ${isDarkMode ? 'bg-red-900/30 text-red-400 border border-red-900/50 hover:bg-red-900/50' : 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'}`}>
                  <RiCloseCircleLine className="me-2" size={18} /> {t('terminate')}
                </button>
              )}

              {tenant.apartmentId && tenant.status !== 'expired' && (
                <button onClick={() => { setPaymentForm({ leaseId: tenant.id, amount: (parseFloat(tenant.balance) > 0 ? tenant.balance : (tenant.rentAfterDiscount || tenant.rent)).toString(), paymentDate: '', paymentPeriodStart: '', paymentPeriodEnd: '', paymentMethod: 'rent', notes: '' }); setShowPaymentForm(true); setError(null); }} className={`px-4 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wider flex items-center transition-all shadow-md bg-emerald-600 text-white hover:bg-emerald-700`}>
                  <RiMoneyDollarCircleLine className="me-2" size={18} /> {t('record_payment')}
                </button>
              )}

              <Link to={`/admin/tenants/${id}/edit`} className={`px-4 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wider flex items-center transition-all border ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700' : 'bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50'}`}>
                <RiPencilLine className="me-2" size={18} /> {t('configure')}
              </Link>
            </div>
          </div>
        </div>

        {/* Dashboard Gauges */}
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            
            <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-800/50 border-zinc-700' : 'bg-zinc-50 border-zinc-200'}`}>
              <div className="flex items-center mb-1">
                <RiBox3Line className={`me-2 text-xl ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('active_unit')}</p>
              </div>
              <p className={`text-2xl font-black font-mono mt-1 ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                {tenant.apartmentNumber ? `${tenant.apartmentNumber}` : t('n_a')}
              </p>
            </div>

            <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-800/50 border-zinc-700' : 'bg-zinc-50 border-zinc-200'}`}>
              <div className="flex items-center mb-1">
                <RiMoneyDollarCircleLine className={`me-2 text-xl ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('base_rate')}</p>
              </div>
              <p className={`text-2xl font-black font-mono mt-1 ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                {formatCurrency(tenant.rent)}
              </p>
            </div>

            <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-800/50 border-zinc-700' : 'bg-zinc-50 border-zinc-200'}`}>
              <div className="flex items-center mb-1">
                <RiCalendarLine className={`me-2 text-xl ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('billing_cycle')}</p>
              </div>
              <p className={`text-2xl font-black font-mono mt-1 ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                {tenant.paymentDate ? t('day_number', { number: tenant.paymentDate }) : t('n_a')}
              </p>
            </div>

            <div className={`p-4 rounded-xl border ${parseFloat(tenant.balance) > 0 ? (isDarkMode ? 'bg-red-900/20 border-red-800/50' : 'bg-red-50 border-red-200') : (isDarkMode ? 'bg-zinc-800/50 border-zinc-700' : 'bg-zinc-50 border-zinc-200')}`}>
              <div className="flex items-center mb-1">
                <RiMoneyDollarCircleLine className={`me-2 text-xl ${parseFloat(tenant.balance) > 0 ? 'text-red-500' : (isDarkMode ? 'text-zinc-500' : 'text-zinc-400')}`} />
                <p className={`text-[10px] font-black uppercase tracking-widest ${parseFloat(tenant.balance) > 0 ? 'text-red-500' : (isDarkMode ? 'text-zinc-500' : 'text-zinc-500')}`}>{t('ledger_debt')}</p>
              </div>
              <p className={`text-2xl font-black font-mono mt-1 ${parseFloat(tenant.balance) > 0 ? (isDarkMode ? 'text-red-400' : 'text-red-600') : (isDarkMode ? 'text-white' : 'text-zinc-900')}`}>
                {formatCurrency(tenant.balance || 0)}
              </p>
            </div>

            <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-emerald-900/20 border-emerald-900/50' : 'bg-emerald-50 border-emerald-200'}`}>
              <div className="flex items-center mb-1">
                <RiShieldCheckLine className={`me-2 text-xl ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-emerald-500' : 'text-emerald-700'}`}>{t('clear_until')}</p>
              </div>
              <p className={`text-xl font-black font-mono mt-2 ${isDarkMode ? 'text-emerald-300' : 'text-emerald-800'}`}>
                {tenant.nextBillingDate ? formatDate(tenant.nextBillingDate) : t('not_set')}
              </p>
            </div>

          </div>
        </div>
      </div>

      {/* Tabs Layout */}
      <div className={`rounded-xl border shadow-lg overflow-hidden ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
        <div className={`border-b ${isDarkMode ? 'border-zinc-800' : 'border-zinc-200'}`}>
          <nav className="flex overflow-x-auto">
            {[
              { id: 'overview', label: t('entity_profile'), icon: RiBriefcaseLine },
              { id: 'leases', label: t('active_contracts'), icon: RiFileListLine },
              { id: 'payments', label: t('financial_ledger'), icon: RiWallet3Line }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-6 py-4 text-sm font-bold uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id
                  ? 'border-red-500 text-red-500'
                  : (isDarkMode ? 'border-transparent text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50' : 'border-transparent text-zinc-500 hover:text-zinc-800 hover:bg-zinc-50')
                  }`}
              >
                <tab.icon className="me-2" size={18} /> {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* TAB 1: OVERVIEW */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                
                {/* Contact Info Block */}
                <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-zinc-800/50 border-zinc-700/50' : 'bg-zinc-50 border-zinc-200/60'}`}>
                  <h3 className={`text-sm font-bold uppercase tracking-widest mb-5 pb-2 border-b flex items-center ${isDarkMode ? 'text-zinc-400 border-zinc-700' : 'text-zinc-500 border-zinc-200'}`}>
                    <RiContactsLine className="me-2 text-lg" /> {t('entity_contacts')}
                  </h3>
                  <div className="space-y-4">
                    {tenant.email && (
                      <div className="flex items-center">
                        <div className={`p-2 rounded-lg me-3 ${isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-white border text-zinc-500'}`}><RiMailLine size={16} /></div>
                        <div>
                          <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('email_address')}</p>
                          <p className={`font-mono font-medium ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}>{tenant.email}</p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center">
                      <div className={`p-2 rounded-lg me-3 ${isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-white border text-zinc-500'}`}><RiPhoneLine size={16} /></div>
                      <div>
                        <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('primary_phone')}</p>
                        <p className={`font-mono font-bold ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}>{tenant.phone}</p>
                      </div>
                    </div>
                    {tenant.preferredLanguage && (
                      <div className="flex items-center">
                        <div className={`p-2 rounded-lg me-3 ${isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-white border text-zinc-500'}`}><RiBriefcaseLine size={16} /></div>
                        <div>
                          <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('comm_language')}</p>
                          <p className={`font-bold ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}>
                            {tenant.preferredLanguage.toUpperCase()}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Logistics Contract Base Info */}
                <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-zinc-800/50 border-zinc-700/50' : 'bg-zinc-50 border-zinc-200/60'}`}>
                  <h3 className={`text-sm font-bold uppercase tracking-widest mb-5 pb-2 border-b flex items-center ${isDarkMode ? 'text-zinc-400 border-zinc-700' : 'text-zinc-500 border-zinc-200'}`}>
                    <RiFileListLine className="me-2 text-lg" /> {t('base_contract_details')}
                  </h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('valid_window')}</p>
                        <p className={`text-sm font-mono font-bold ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}>
                          {formatDate(tenant.contractStartDate)}<br/>{t('to')} {formatDate(tenant.contractEndDate)}
                        </p>
                      </div>
                      <div>
                        <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('clear_until')}</p>
                        <p className={`text-sm font-mono font-bold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                          {tenant.nextBillingDate ? formatDate(tenant.nextBillingDate) : t('n_a')}
                        </p>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-zinc-500/20">
                      <div className="flex justify-between items-center py-1">
                        <span className={`text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('gross_rate')}</span>
                        <span className={`font-mono font-bold ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}>{formatCurrency(tenant.rent)}</span>
                      </div>
                      {tenant.discount > 0 && (
                        <div className="flex justify-between items-center py-1">
                          <span className={`text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{t('applied_discount')}</span>
                          <span className={`font-mono font-bold ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>- {formatCurrency(tenant.discount)}</span>
                        </div>
                      )}
                      {tenant.rentAfterDiscount && (
                        <div className="flex justify-between items-center py-1 mt-1 border-t border-zinc-500/20 pt-2">
                          <span className={`text-xs font-black uppercase tracking-wider ${isDarkMode ? 'text-zinc-300' : 'text-zinc-800'}`}>{t('net_effective_rate')}</span>
                          <span className={`font-mono font-black text-lg ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{formatCurrency(tenant.rentAfterDiscount)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Additional Surcharges */}
                <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-zinc-800/50 border-zinc-700/50' : 'bg-zinc-50 border-zinc-200/60'}`}>
                  <h3 className={`text-sm font-bold uppercase tracking-widest mb-5 pb-2 border-b flex items-center ${isDarkMode ? 'text-zinc-400 border-zinc-700' : 'text-zinc-500 border-zinc-200'}`}>
                    <RiWallet3Line className="me-2 text-lg" /> {t('surcharges_deposits')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-900/50' : 'bg-white border'}`}>
                      <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('service_fee')}</p>
                      <p className={`font-mono font-bold mt-1 ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}>{formatCurrency(tenant.serviceFee || 0)}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-900/50' : 'bg-white border'}`}>
                      <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('commission')}</p>
                      <p className={`font-mono font-bold mt-1 ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}>{formatCurrency(tenant.officeCommission || 0)}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-900/50' : 'bg-white border'}`}>
                      <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('sec_deposit')}</p>
                      <p className={`font-mono font-bold mt-1 ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}>{formatCurrency(tenant.insurance || 0)}</p>
                    </div>
                  </div>
                </div>

                {/* Client Notes */}
                {tenant.notes && (
                  <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-zinc-800/50 border-zinc-700/50' : 'bg-zinc-50 border-zinc-200/60'}`}>
                    <h3 className={`text-sm font-bold uppercase tracking-widest mb-4 pb-2 border-b flex items-center ${isDarkMode ? 'text-zinc-400 border-zinc-700' : 'text-zinc-500 border-zinc-200'}`}>
                      <RiFileTextLine className="me-2 text-lg" /> {t('entity_notes')}
                    </h3>
                    <p className={`text-sm font-medium leading-relaxed ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>{tenant.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 2: CONTRACTS / LEASES */}
          {activeTab === 'leases' && (
            <div>
              <div className="flex justify-between items-end mb-6 border-b pb-4 border-zinc-500/20">
                <h3 className={`text-xl font-black uppercase tracking-tight ${isDarkMode ? 'text-white' : 'text-zinc-800'}`}>
                  {t('contract_registry')} <span className={`text-sm font-bold ms-2 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>({t('logs_count', { count: leases.length })})</span>
                </h3>
              </div>

              {leases.length === 0 ? (
                <div className="text-center py-16">
                  <RiFileListLine className={`mx-auto text-6xl mb-4 ${isDarkMode ? 'text-zinc-700' : 'text-zinc-300'}`} />
                  <h3 className={`text-xl font-bold mb-2 uppercase tracking-wide ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('no_contracts_found')}</h3>
                  <p className={`font-medium ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>
                    {t('no_contracts_desc')}
                  </p>
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Active Contracts */}
                  {activeLeases.length > 0 && (
                    <div>
                      <h4 className={`text-[10px] font-black uppercase tracking-[0.2em] mb-4 flex items-center ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        <div className="h-1.5 w-1.5 rounded-full bg-current me-2 animate-pulse" /> {t('active_contracts')}
                      </h4>
                      <div className="space-y-4">
                        {activeLeases.map((lease) => {
                          const leaseSummary = calculateLeaseSummary(lease);
                          return (
                            <div key={lease.id} className={`p-6 rounded-xl border shadow-sm ${isDarkMode ? 'bg-zinc-800/80 border-zinc-700' : 'bg-white border-zinc-200'}`}>
                              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-4 border-b border-zinc-500/20">
                                <div>
                                  <div className="flex items-center gap-3">
                                    <h4 className={`text-2xl font-black font-mono tracking-tight ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                                      {t('unit_number_display', { number: lease.property_number || tenant.apartmentNumber })}
                                    </h4>
                                    <span className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-widest rounded border ${isDarkMode ? 'bg-emerald-900/30 text-emerald-400 border-emerald-900' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}`}>
                                      {t('active')}
                                    </span>
                                    {leaseSummary.isExpiringSoon && (
                                      <span className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-widest rounded border ${isDarkMode ? 'bg-amber-900/30 text-amber-500 border-amber-900' : 'bg-amber-100 text-amber-700 border-amber-200'}`}>
                                        {t('expiring_soon_label')}
                                      </span>
                                    )}
                                  </div>
                                  <p className={`text-sm font-medium mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                                    <RiBuilding3Line className="inline me-1" /> {lease.building_name || tenant.buildingName}
                                  </p>
                                </div>
                                <button onClick={() => setEndLeaseModal(true)} className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border transition-colors ${isDarkMode ? 'text-red-400 border-red-900 hover:bg-red-900/30' : 'text-red-600 border-red-200 hover:bg-red-50'}`} title={t('terminate')}>
                                  {t('terminate')}
                                </button>
                              </div>

                              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
                                <div>
                                  <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('valid_from')}</p>
                                  <p className={`text-sm font-mono font-bold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{formatDate(lease.start_date || tenant.contractStartDate)}</p>
                                </div>
                                <div>
                                  <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('valid_to')}</p>
                                  <p className={`text-sm font-mono font-bold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{formatDate(lease.end_date || tenant.contractEndDate)}</p>
                                </div>
                                <div>
                                  <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('total_term')}</p>
                                   {t('total_term')} • <span className={`${isDarkMode ? 'text-zinc-300' : 'text-zinc-900'}`}>{leaseSummary.durationMonths} {t('months')}</span>
                                </div>
                                <div>
                                  <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('remaining')}</p>
                                   {t('remaining')} • <span className={`${isDarkMode ? 'text-zinc-300' : 'text-zinc-900'}`}>{leaseSummary.remainingMonths} {t('months')}</span>
                                </div>
                              </div>

                              <div className={`p-4 rounded-xl grid grid-cols-2 md:grid-cols-4 gap-4 items-center ${isDarkMode ? 'bg-zinc-900/50' : 'bg-zinc-50'}`}>
                                <div>
                                  <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('base_rate')}</p>
                                  <p className={`text-sm font-mono font-bold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>{formatCurrency(lease.monthly_rent || tenant.rent)}</p>
                                </div>
                                <div>
                                  <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('sec_deposit')}</p>
                                  <p className={`text-sm font-mono font-bold ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>{formatCurrency(lease.security_deposit || tenant.insurance)}</p>
                                </div>
                                <div>
                                  <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-emerald-500' : 'text-emerald-700'}`}>{t('total_value')}</p>
                                  <p className={`text-sm font-mono font-bold ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>{formatCurrency(leaseSummary.totalLeaseValue)}</p>
                                </div>
                                <div className="md:text-end mt-2 md:mt-0">
                                  <button onClick={() => { setPaymentForm({ leaseId: lease.id, amount: (parseFloat(tenant.balance) > 0 ? tenant.balance : (lease.monthly_rent || tenant.rent)).toString(), paymentDate: '', paymentPeriodStart: '', paymentPeriodEnd: '', paymentMethod: 'rent', notes: '' }); setShowPaymentForm(true); setError(null); }} className={`flex items-center text-xs font-black uppercase tracking-wider py-1.5 px-3 rounded border transition-all ${isDarkMode ? 'bg-emerald-900/30 text-emerald-400 border-emerald-900/50 hover:bg-emerald-900/50' : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'}`}>
                                    <RiMoneyDollarCircleLine className="me-1.5" size={14} /> {t('log_payment')}
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Historical Contracts */}
                  {leases.filter(lease => lease.status !== 'active').length > 0 && (
                    <div>
                      <h4 className={`text-[10px] font-black uppercase tracking-[0.2em] mb-4 mt-8 flex items-center ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
                        <div className="h-1.5 w-1.5 rounded-full bg-current me-2" /> {t('historical_contracts')}
                      </h4>
                      <div className="space-y-4">
                        {leases.filter(lease => lease.status !== 'active').map((lease) => (
                          <div key={lease.id} className={`p-6 rounded-xl border ${isDarkMode ? 'bg-zinc-900/50 border-zinc-800' : 'bg-zinc-50 border-zinc-200'}`}>
                            <div className="flex justify-between items-center mb-4">
                              <div className="flex items-center gap-3">
                                <h4 className={`text-lg font-black font-mono tracking-tight ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>
                                  {t('unit_number_display', { number: lease.property_number || tenant.apartmentNumber })}
                                </h4>
                                <span className={`px-2 py-0.5 text-[9px] font-black uppercase tracking-widest rounded border ${isDarkMode ? 'bg-zinc-800 text-zinc-500 border-zinc-700' : 'bg-zinc-200 text-zinc-500 border-zinc-300'}`}>
                                  {t('ended')}
                                </span>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div>
                                <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('from')}</p>
                                <p className={`text-sm font-mono font-bold ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{formatDate(lease.start_date || tenant.contractStartDate)}</p>
                              </div>
                              <div>
                                <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('to')}</p>
                                <p className={`text-sm font-mono font-bold ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{formatDate(lease.end_date || tenant.contractEndDate)}</p>
                              </div>
                              {(lease.actual_end_date || tenant.paymentEndDate) && (
                                <div>
                                  <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isDarkMode ? 'text-zinc-600' : 'text-zinc-400'}`}>{t('terminated')}</p>
                                  <p className={`text-sm font-mono font-bold ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{formatDate(lease.actual_end_date || tenant.paymentEndDate)}</p>
                                </div>
                              )}
                              <div>
                                <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('base_rate')}</p>
                                <p className={`text-sm font-mono font-bold ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{formatCurrency(lease.monthly_rent || tenant.rent)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: PAYMENTS / LEDGER */}
          {activeTab === 'payments' && (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 border-b pb-4 border-zinc-500/20 gap-4">
                <h3 className={`text-xl font-black uppercase tracking-tight ${isDarkMode ? 'text-white' : 'text-zinc-800'}`}>
                  {t('financial_ledger')} <span className={`text-sm font-bold ms-2 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>({t('logs_count', { count: payments.length })})</span>
                </h3>
                {tenant.apartmentId && (
                  <button onClick={() => { setPaymentForm({ leaseId: tenant.id, amount: tenant.rent.toString(), paymentDate: '', paymentPeriodStart: '', paymentPeriodEnd: '', paymentMethod: 'rent', notes: '' }); setShowPaymentForm(true); setError(null); }} className="px-5 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-emerald-600 text-white hover:bg-emerald-700 shadow-md flex items-center transition-all">
                    <RiAddLine className="me-2" size={16} /> {t('log_transaction')}
                  </button>
                )}
              </div>

              {payments.length === 0 ? (
                <div className="text-center py-16">
                  <RiWallet3Line className={`mx-auto text-6xl mb-4 ${isDarkMode ? 'text-zinc-700' : 'text-zinc-300'}`} />
                  <h3 className={`text-lg font-bold uppercase tracking-wider mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('no_transactions_found')}</h3>
                  <p className={`text-sm font-medium mb-6 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('no_transactions_desc')}</p>
                  {tenant.apartmentId && (
                    <button onClick={() => { setPaymentForm({ leaseId: tenant.id, amount: tenant.rent.toString(), paymentDate: '', paymentPeriodStart: '', paymentPeriodEnd: '', paymentMethod: 'rent', notes: '' }); setShowPaymentForm(true); setError(null); }} className="px-6 py-3 rounded-lg text-sm font-bold uppercase tracking-wider bg-emerald-600 text-white hover:bg-emerald-700 shadow-md transition-all inline-flex items-center">
                      <RiAddLine className="me-2" size={18} /> {t('initialize_ledger')}
                    </button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-start whitespace-nowrap">
                    <thead>
                      <tr className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'bg-zinc-900/50 text-zinc-500 border-b border-zinc-800' : 'bg-zinc-50 text-zinc-500 border-b border-zinc-200'}`}>
                        <th className="px-6 py-4">{t('transaction_amount')}</th>
                        <th className="px-6 py-4">{t('type')}</th>
                        <th className="px-6 py-4">{t('log_date')}</th>
                        <th className="px-6 py-4">{t('covered_period')}</th>
                        <th className="px-6 py-4">{t('ref_notes')}</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDarkMode ? 'divide-zinc-800' : 'divide-zinc-100'}`}>
                      {payments.map((payment) => (
                        <tr key={payment.id} className={`transition-colors ${isDarkMode ? 'hover:bg-zinc-800/30' : 'hover:bg-zinc-50/50'}`}>
                          <td className="px-6 py-4">
                            <span className={`font-mono font-black text-lg ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                              {formatCurrency(payment.amount)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex px-2.5 py-1 text-[9px] font-black uppercase tracking-widest rounded border ${isDarkMode ? 'bg-zinc-800 text-zinc-300 border-zinc-700' : 'bg-zinc-100 text-zinc-700 border-zinc-300'}`}>
                              {payment.paymentType ? t(payment.paymentType.toLowerCase()) : t('rent')}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`font-mono text-sm font-bold ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
                              {formatDate(payment.paymentDate)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`font-mono text-xs font-bold ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>
                              {formatPaymentPeriod(payment)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`text-xs font-medium max-w-[200px] truncate block ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>
                              {payment.notes || '-'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Payment Form Modal */}
      {showPaymentForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className={`relative max-w-md w-full p-6 rounded-xl border shadow-2xl ${isDarkMode ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-zinc-200'}`}>
            {saving && (
              <div className={`absolute inset-0 z-10 flex flex-col items-center justify-center rounded-xl ${isDarkMode ? 'bg-zinc-900/80' : 'bg-white/80'} backdrop-blur-sm`}>
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
                <p className={`mt-4 font-bold uppercase tracking-widest text-xs ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{t('processing_ledger')}</p>
              </div>
            )}

            <div className={`flex items-center mb-6 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
              <RiMoneyDollarCircleLine size={24} className="me-2" />
              <h3 className="text-lg font-bold uppercase tracking-wider">{t('log_transaction')}</h3>
            </div>

            {error && (
              <div className={`p-4 mb-6 rounded-lg text-sm font-bold flex items-center gap-2 ${isDarkMode ? 'bg-red-900/20 text-red-400 border border-red-900/50' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                <RiAlertLine size={18} className="shrink-0" /> {error}
              </div>
            )}

            <form onSubmit={handlePaymentSubmit} className="space-y-5">
              <input type="hidden" name="leaseId" value={paymentForm.leaseId || tenant.id} />

              <div>
                <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('amount')} *</label>
                <input type="number" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} className={`w-full p-3 rounded-lg border font-mono font-bold transition-all focus:ring-2 focus:ring-emerald-500/50 ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`} required />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('log_date')} *</label>
                  <div className="relative">
                    <DatePicker selected={paymentForm.paymentDate ? new Date(paymentForm.paymentDate) : null} onChange={(date) => setPaymentForm({ ...paymentForm, paymentDate: formatDateForBackend(date) })} dateFormat="MM/dd/yyyy" placeholderText="mm/dd/yyyy" className={`w-full p-3 rounded-lg border font-mono font-bold transition-all ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`} required portalId="root-portal" />
                    <RiCalendarLine className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none`} />
                  </div>
                </div>
                <div>
                  <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('type')}</label>
                  <select value={paymentForm.paymentMethod} onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })} className={`w-full p-3 rounded-lg border font-bold text-sm transition-all ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`}>
                    <option value="rent">{t('rent')}</option>
                    <option value="service_fee">{t('service_fee')}</option>
                    <option value="insurance">{t('sec_deposit')}</option>
                    <option value="other">{t('other')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('covered_period')} *</label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('from')}</label>
                    <div className="relative">
                      <DatePicker selected={paymentForm.paymentPeriodStart ? new Date(paymentForm.paymentPeriodStart) : null} onChange={(date) => setPaymentForm({ ...paymentForm, paymentPeriodStart: formatDateForBackend(date) })} dateFormat="MM/dd/yyyy" placeholderText="mm/dd/yyyy" className={`w-full p-2.5 rounded-lg border font-mono text-sm transition-all ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`} required portalId="root-portal" />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('to')}</label>
                    <div className="relative">
                      <DatePicker selected={paymentForm.paymentPeriodEnd ? new Date(paymentForm.paymentPeriodEnd) : null} onChange={(date) => setPaymentForm({ ...paymentForm, paymentPeriodEnd: formatDateForBackend(date) })} dateFormat="MM/dd/yyyy" placeholderText="mm/dd/yyyy" className={`w-full p-2.5 rounded-lg border font-mono text-sm transition-all ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`} required portalId="root-portal" />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('ref_notes')}</label>
                <textarea value={paymentForm.notes} onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })} rows={2} className={`w-full p-3 rounded-lg border text-sm transition-all focus:ring-2 focus:ring-emerald-500/50 ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`} />
              </div>

               <div className="flex justify-end gap-3 pt-4 border-t border-zinc-500/20">
                <button type="button" onClick={() => { setShowPaymentForm(false); setError(null); }} disabled={saving} className={`px-5 py-2.5 rounded-lg font-bold transition-colors ${isDarkMode ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-zinc-200 text-zinc-700 hover:bg-zinc-300'}`}>
                  {t('cancel')}
                </button>
                <button type="submit" disabled={saving} className="px-6 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-wider shadow-md transition-colors flex items-center">
                  {t('finalize_transaction')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Extend Contract Modal */}
      {showExtendModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className={`max-w-md w-full rounded-xl border shadow-2xl p-6 ${isDarkMode ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-zinc-200'}`}>
            <div className={`flex items-center mb-6 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
              <RiCalendar2Line size={24} className="me-2" />
              <h3 className="text-xl font-bold uppercase tracking-wider">{t('extend_contract')}</h3>
            </div>
            <form onSubmit={handleExtendLease} className="space-y-5">
              <div>
                <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('current_end_date')}</label>
                <div className={`p-3 rounded-lg border font-mono font-bold ${isDarkMode ? 'bg-zinc-800/50 border-zinc-700 text-zinc-500' : 'bg-zinc-50 border-zinc-200 text-zinc-400'}`}>
                  {formatDate(tenant.contractEndDate)}
                </div>
              </div>
              <div>
                <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('new_end_date')} *</label>
                <div className="relative">
                  <DatePicker selected={extensionForm.newEndDate ? new Date(extensionForm.newEndDate) : null} onChange={(date) => setExtensionForm({ ...extensionForm, newEndDate: formatDateForBackend(date) })} dateFormat="MM/dd/yyyy" placeholderText="mm/dd/yyyy" className={`w-full p-3 rounded-lg border font-mono font-bold transition-all focus:ring-2 focus:ring-blue-500/50 ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`} required portalId="root-portal" />
                  <RiCalendarLine className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none`} />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-5 border-t border-zinc-500/20">
                <button type="button" onClick={() => setShowExtendModal(false)} disabled={extendingLease} className={`px-5 py-2.5 rounded-lg font-bold transition-colors ${isDarkMode ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-zinc-200 text-zinc-700 hover:bg-zinc-300'}`}>
                  {t('cancel')}
                </button>
                <button type="submit" disabled={extendingLease} className="px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold uppercase tracking-wider shadow-md transition-colors flex items-center">
                  {extendingLease ? <><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin me-2" />{t('extending_contract')}</> : t('confirm_extension')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Renew Contract Modal */}
      {showRenewModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className={`max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-xl border shadow-2xl p-6 ${isDarkMode ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-zinc-200'}`}>
            <div className={`flex items-center mb-6 pb-4 border-b border-zinc-500/20 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
              <RiRefreshLine size={24} className="me-2" />
              <h3 className="text-xl font-black uppercase tracking-wider">{t('renew_logistics_contract')}</h3>
            </div>
            <form onSubmit={handleRenewLease} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('allocated_unit')} *</label>
                  <select value={renewForm.apartmentId} onChange={(e) => setRenewForm({ ...renewForm, apartmentId: e.target.value })} className={`w-full p-3 rounded-lg border font-bold transition-all focus:ring-2 focus:ring-blue-500/50 ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`} required>
                    <option value="">{t('select_target_unit')}</option>
                    {tenant.apartmentId && <option value={tenant.apartmentId}>{tenant.apartmentNumber} ({t('current_unit')})</option>}
                    {apartments.filter(a => a.id !== tenant.apartmentId).map(apt => (
                      <option key={apt.id} value={apt.id}>{apt.apartment_number || apt.apartmentNumber} - {apt.building_name || apt.buildingName}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('new_base_rate')} *</label>
                  <input type="number" value={renewForm.rent} onChange={(e) => setRenewForm({ ...renewForm, rent: e.target.value })} className={`w-full p-3 rounded-lg border font-mono font-bold transition-all focus:ring-2 focus:ring-blue-500/50 ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`} required />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('start_date')} *</label>
                  <DatePicker selected={renewForm.contractStartDate ? new Date(renewForm.contractStartDate) : null} onChange={(date) => setRenewForm({ ...renewForm, contractStartDate: formatDateForBackend(date) })} dateFormat="MM/dd/yyyy" className={`w-full p-3 rounded-lg border font-mono font-bold transition-all ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`} required portalId="root-portal" />
                </div>
                <div>
                  <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('end_date')} *</label>
                  <DatePicker selected={renewForm.contractEndDate ? new Date(renewForm.contractEndDate) : null} onChange={(date) => setRenewForm({ ...renewForm, contractEndDate: formatDateForBackend(date) })} dateFormat="MM/dd/yyyy" className={`w-full p-3 rounded-lg border font-mono font-bold transition-all ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`} required portalId="root-portal" />
                </div>
                <div>
                  <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('billing_cycle_start')} *</label>
                  <DatePicker selected={renewForm.paymentDate ? new Date(renewForm.paymentDate) : null} onChange={(date) => setRenewForm({ ...renewForm, paymentDate: formatDateForBackend(date) })} dateFormat="MM/dd/yyyy" className={`w-full p-3 rounded-lg border font-mono font-bold transition-all ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`} required portalId="root-portal" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('service_fee')}</label>
                  <input type="number" value={renewForm.serviceFee} onChange={(e) => setRenewForm({ ...renewForm, serviceFee: e.target.value })} className={`w-full p-3 rounded-lg border font-mono font-bold ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`} />
                </div>
                <div>
                  <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('commission')}</label>
                  <input type="number" value={renewForm.officeCommission} onChange={(e) => setRenewForm({ ...renewForm, officeCommission: e.target.value })} className={`w-full p-3 rounded-lg border font-mono font-bold ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`} />
                </div>
                <div>
                  <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('sec_deposit')}</label>
                  <input type="number" value={renewForm.insurance} onChange={(e) => setRenewForm({ ...renewForm, insurance: e.target.value })} className={`w-full p-3 rounded-lg border font-mono font-bold ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`} />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-zinc-500/20">
                <button type="button" onClick={() => setShowRenewModal(false)} disabled={renewingLease} className={`px-5 py-2.5 rounded-lg font-bold transition-colors ${isDarkMode ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-zinc-200 text-zinc-700 hover:bg-zinc-300'}`}>
                  {t('cancel')}
                </button>
                <button type="submit" disabled={renewingLease} className="px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold uppercase tracking-wider shadow-md transition-colors flex items-center">
                  {renewingLease ? <><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin me-2" />{t('processing')}</> : t('finalize_renewal')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Relocate Unit Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className={`max-w-md w-full rounded-xl border shadow-2xl p-6 ${isDarkMode ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-zinc-200'}`}>
            <div className={`flex items-center mb-6 pb-4 border-b border-zinc-500/20 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
              <RiHistoryLine size={24} className="me-2" />
              <h3 className="text-xl font-black uppercase tracking-wider">{t('relocate_client_unit')}</h3>
            </div>
            <form onSubmit={handleTransferApartment} className="space-y-5">
              <div>
                <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('current_allocation')}</label>
                <div className={`p-3 rounded-lg border font-mono font-bold ${isDarkMode ? 'bg-zinc-800/50 border-zinc-700 text-zinc-500' : 'bg-zinc-50 border-zinc-200 text-zinc-400'}`}>
                  {t('unit_number_display', { number: tenant.apartmentNumber })} • {tenant.buildingName}
                </div>
              </div>
              <div>
                <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('allocated_unit')} *</label>
                <select value={transferForm.apartmentId} onChange={(e) => setTransferForm({ ...transferForm, apartmentId: e.target.value })} className={`w-full p-3 rounded-lg border font-bold transition-all focus:ring-2 focus:ring-emerald-500/50 ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`} required>
                  <option value="">{t('select_target_slot')}</option>
                  {apartments.map(apt => (
                    <option key={apt.id} value={apt.id}>{apt.apartment_number || apt.apartmentNumber} - {apt.building_name || apt.buildingName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('new_base_rate')}</label>
                <input type="number" value={transferForm.rent} onChange={(e) => setTransferForm({ ...transferForm, rent: e.target.value })} placeholder={t('base_rate')} className={`w-full p-3 rounded-lg border font-mono font-bold transition-all focus:ring-2 focus:ring-emerald-500/50 ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-600' : 'bg-white border-zinc-300 text-zinc-900 placeholder-zinc-400'}`} />
              </div>
              <div className="flex justify-end gap-3 pt-5 border-t border-zinc-500/20">
                <button type="button" onClick={() => setShowTransferModal(false)} disabled={transferringApartment} className={`px-5 py-2.5 rounded-lg font-bold transition-colors ${isDarkMode ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-zinc-200 text-zinc-700 hover:bg-zinc-300'}`}>
                  {t('cancel')}
                </button>
                <button type="submit" disabled={transferringApartment} className="px-6 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-wider shadow-md transition-colors flex items-center">
                  {transferringApartment ? <><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin me-2" />{t('processing')}</> : t('confirm_relocation')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantDetailView;